import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { TillFigure } from "@/features/staff/till-figure"
import { formatRate, tTill, type ShiftReport } from "@/features/staff/till"

/**
 * The shift's cut: what was collected, on which rail, what tax sat inside it,
 * and what settled while this shift was running but belonged to no shift at
 * all.
 *
 * **This report is not the day.** A shift covers the stretch between someone
 * opening the till and someone closing it, and the diner's QR rail keeps
 * settling money outside those hours — deliberately, because refusing a diner
 * at a table because nobody opened a register turns an operational slip into a
 * lost sale. So there is a permanent, unconditional sentence saying what this
 * total does and does not cover, and money that landed with no shift attached
 * gets its own line instead of being folded in or quietly dropped. Both
 * readings are wrong in the same expensive way: a restaurant that reconciles
 * this cut against its bank and finds it short goes looking for a thief.
 *
 * The out-of-shift line appears only when there is something to report. A
 * permanent "$0.00 collected outside the shift" is noise on a screen read at
 * speed, and the sentence above already establishes the scope.
 *
 * The tax block never renders a zero it cannot stand behind. `forward_regime`
 * and `not_recorded` are answers, not amounts: a location that adds tax on top
 * of menu prices freezes no lines by design, and a shift whose attempts predate
 * the tax snapshot has none to show. Printing "0.00" for either is how a filing
 * error starts.
 */

type TillReportPanelProps = {
  report: ShiftReport
  timeZone: string
}

function Row({
  label,
  meta,
  amount,
  testId,
}: {
  label: string
  meta?: string
  amount: string
  testId?: string
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2" data-testid={testId}>
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      {meta ? (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span>
      ) : null}
      <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{amount}</span>
    </li>
  )
}

export function TillReportPanel({ report, timeZone }: TillReportPanelProps) {
  const closed = report.shift.status === "closed"
  const outside = report.outOfShift
  const tax = report.tax
  const refunds = report.refunds
  const discounts = report.discounts
  const cancellations = report.cancellations

  return (
    // The testid carries the status on purpose, so a spec that means the signed
    // Z cannot match the live X by accident.
    //
    // Both are this same component, and after a close the two overlap in time:
    // the close submits with `preserveState`, so the OPEN shift's panel is still
    // mounted for the beat between the PATCH answering and React re-rendering on
    // the new props -- carrying the same figures, which is exactly what made
    // reading it look like it worked. `tests/staff/discounts.spec.ts` did read
    // it, passed on those figures, and then failed on whichever assertion
    // happened to land after the unmount (CI run 32783804511). One name for two
    // states is what let that be written; two names make it unwritable.
    <Card density="compact" data-testid={closed ? "shift-report-closed" : "shift-report-open"}>
      <CardHeader className="border-b">
        <CardTitle>{tTill(closed ? "report_title_closed" : "report_title_open")}</CardTitle>
        <CardDescription>
          {tTill("report_scope")}
          {closed && report.shift.closedAt ? (
            <>
              {" "}
              {tTill("closed_by", {
                name: report.shift.closedBy ?? tTill("unknown_actor"),
                time: formatDateTime(report.shift.closedAt, { timeZone }),
              })}
            </>
          ) : null}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* A closed shift carries its whole cash chain here, because the card
            that showed it live is gone: this is the only place the signed
            numbers still exist. */}
        {closed ? (
          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <TillFigure
              label={tTill("opening_float_label")}
              value={formatMoney(report.openingFloat)}
            />
            <TillFigure label={tTill("cash_sales")} value={formatMoney(report.cashSales)} />
            <TillFigure
              label={tTill("movements_total")}
              value={formatMoney(report.movementsTotal)}
            />
            <TillFigure
              label={tTill("expected_cash")}
              value={formatMoney(report.expectedCash)}
              testId="shift-expected-cash"
            />
            <TillFigure
              label={tTill("declared_label")}
              value={report.declaredCash === null ? "—" : formatMoney(report.declaredCash)}
            />
            <TillFigure
              label={tTill("difference_label")}
              value={report.difference === null ? "—" : formatMoney(report.difference)}
              testId="shift-difference"
              emphasis
            />
          </section>
        ) : null}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{tTill("payment_methods_title")}</h3>
          {report.paymentMethods.length === 0 ? (
            <p className="text-sm text-muted-foreground">{tTill("payment_methods_empty")}</p>
          ) : (
            <ul className="divide-y rounded-md border" data-testid="shift-payment-methods">
              {report.paymentMethods.map((entry) => (
                <Row
                  key={entry.paymentMethod}
                  testId={`shift-payment-method-${entry.paymentMethod}`}
                  label={entry.paymentMethodLabel}
                  meta={tTill("payments_count", { count: entry.count })}
                  amount={formatMoney(entry.collected)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* A signed Z lists the drawer's non-sale movements; the live card that
            listed them one by one is gone by then. Rolled up by kind and reason
            rather than repeated row by row, because at this point the question
            is what left the drawer and why, not in which order. */}
        {closed ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{tTill("movements_title")}</h3>
            {report.movementGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground">{tTill("movements_empty_closed")}</p>
            ) : (
              <ul className="divide-y rounded-md border" data-testid="shift-movement-groups">
                {report.movementGroups.map((group) => (
                  <Row
                    key={`${group.kind}-${group.reason}`}
                    label={`${group.kindLabel} · ${group.reasonLabel}`}
                    meta={tTill("movements_count", { count: group.count })}
                    amount={formatMoney(group.amount)}
                  />
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {/* Reversals, giveaways and cancellations. All three name money; none
            of them deducts it.

            Rendered only when there is something to report. An empty
            "Descuentos $0.00" is a decorative dead control on a screen read at
            speed — the same rule the out-of-shift line follows below, and the
            reason none of these carries an empty state. */}
        {refunds.count > 0 ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{tTill("refunds_title")}</h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <TillFigure
                label={tTill("refunds_drawer")}
                value={formatMoney(refunds.drawerTotal)}
                testId="shift-refunds-drawer"
              />
              <TillFigure
                label={tTill("refunds_provider")}
                value={formatMoney(refunds.providerTotal)}
                testId="shift-refunds-provider"
              />
            </div>
            <ul className="divide-y rounded-md border" data-testid="shift-refund-groups">
              {refunds.groups.map((group) => (
                <Row
                  key={`${group.rail}-${group.reason}`}
                  label={group.reasonLabel}
                  meta={tTill("refunds_count", { count: group.count })}
                  amount={formatMoney(group.amount)}
                />
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{tTill("refunds_hint")}</p>
          </section>
        ) : null}

        {/* Before taxes, because a discount reduces the base the tax was
            extracted from and the report should read in that order. */}
        {discounts.count > 0 ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{tTill("discounts_title")}</h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <TillFigure
                label={tTill("discounts_revenue")}
                value={formatMoney(discounts.revenueTotal)}
                testId="shift-discounts-revenue"
              />
              <TillFigure
                label={tTill("discounts_non_revenue")}
                value={formatMoney(discounts.nonRevenueTotal)}
                testId="shift-discounts-non-revenue"
              />
            </div>
            <ul className="divide-y rounded-md border" data-testid="shift-discount-groups">
              {discounts.groups.map((group) => (
                <Row
                  key={`${group.kind}-${group.reason}-${String(group.countsAsRevenue)}`}
                  label={`${group.kindLabel} · ${group.reasonLabel}`}
                  meta={tTill("discounts_count", { count: group.count })}
                  amount={formatMoney(group.amount)}
                />
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{tTill("discounts_hint")}</p>
          </section>
        ) : null}

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{tTill("taxes_title")}</h3>
          {tax.status === "itemized" && tax.lines.length > 0 ? (
            <ul className="divide-y rounded-md border" data-testid="shift-tax-lines">
              {tax.lines.map((line) => (
                <Row
                  key={`${line.name}-${line.rate}`}
                  label={line.name}
                  meta={formatRate(line.rate)}
                  amount={formatMoney(line.amount)}
                />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="shift-tax-status">
              {tTill(`tax_status_${tax.status}`)}
            </p>
          )}
          {tax.attemptsWithoutBreakdown > 0 ? (
            <p className="text-xs text-muted-foreground">
              {tTill("tax_missing_breakdown", { count: tax.attemptsWithoutBreakdown })}
            </p>
          ) : null}
        </section>

        {cancellations.count > 0 ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">{tTill("cancellations_title")}</h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <TillFigure
                label={tTill("cancellations_total")}
                value={formatMoney(cancellations.total)}
                testId="shift-cancellations-total"
              />
              <TillFigure
                label={tTill("cancellations_bills")}
                value={String(cancellations.billCount)}
                testId="shift-cancellations-bills"
              />
              <TillFigure
                label={tTill("cancellations_orders")}
                value={String(cancellations.orderCount)}
                testId="shift-cancellations-orders"
              />
            </div>
            <ul className="divide-y rounded-md border" data-testid="shift-cancellation-groups">
              {cancellations.groups.map((group) => (
                <Row
                  key={`${group.scope}-${group.reason ?? "unspecified"}`}
                  label={`${group.scopeLabel} · ${group.reasonLabel}`}
                  meta={tTill("cancellations_count", { count: group.count })}
                  amount={formatMoney(group.amount)}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {outside.count > 0 ? (
          <section
            className="space-y-1 rounded-md border bg-muted/40 px-4 py-3"
            data-testid="shift-outside-shift"
          >
            <div className="flex items-baseline gap-3">
              <span className="min-w-0 flex-1 text-sm font-medium">
                {tTill("outside_shift_title")}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {tTill("payments_count", { count: outside.count })}
              </span>
              <span
                className="shrink-0 text-sm font-semibold tabular-nums text-foreground"
                data-testid="shift-outside-total"
              >
                {formatMoney(outside.collected)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{tTill("outside_shift_hint")}</p>
          </section>
        ) : null}
      </CardContent>
    </Card>
  )
}
